import React, { useState, useContext } from 'react';
import { Box, Tab, Link, Typography } from '@mui/material/';
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { CheckCircle, Delete } from "@mui/icons-material";
import AppContext from "../utils/app-context.js";
import { AuthContext } from "../utils/auth-provider.js";
import Item from '@mui/material/Unstable_Grid2'
import ZebraPlayer from './zebra-player.js';
import DropUpload from './drop-upload.js';
import toast from 'react-hot-toast';
import ConfirmDialog from './dialogs/confirm.js';
import { persistUserState } from '../helpers/index.js';

const delStyles = {
  position: 'absolute',
  top: "2px",
  right: "2px",
  opacity: 0,
  color: "#ccc",
  zIndex: 5,
  transition: "all 0.3s ease-in-out",
  "&:hover": {
    color: "red",
  }
};

/**
 * RefManager
 *
 * Props:
 *  - shotId           {string}           Shot to attach the reference to.
 *  - maxHeight        {string}           CSS max-height for the scrollable grids.
 *  - addUserRef       {boolean}          Whether to persist refs to userRefs.
 *  - totalRefs        {number}           Max active refs allowed.
 *  - callback         {Function}         Called after a ref is persisted (immediate-commit mode).
 *
 * Deferred / multi-select mode (used by RefsDialog with Save/Cancel):
 *  - pendingIds       {Set<string>}      Controlled set of staged take IDs. If a Set is provided,
 *                                        the component operates in deferred mode — clicks toggle
 *                                        membership rather than immediately persisting.
 *  - onPendingChange  {Function(Set)}    Called when the pending selection changes.
 *  - onUpload         {Function({take})} Called when a file upload produces a new take (deferred).
 */
export default function RefManager(props) {
  const {
    shotId,
    maxHeight,
    addUserRef = false,
    totalRefs,
    callback,
    pendingIds,
    onPendingChange,
    onUpload,
  } = props;

  const { state, dispatch } = useContext(AppContext);
  const { currentUser } = useContext(AuthContext);
  const [tabValue, setTabValue] = useState('refs');
  const [showVid, setShowVid] = useState({});
  const [vidPlay, setVidPlay] = useState({});
  const [vidHover, setVidHover] = useState({});
  const [playTake, setPlayTake] = useState({});
  const [confirmDeleteRef, setConfirmDeleteRef] = useState(null);

  // Deferred mode is active when the parent passes a Set for pendingIds.
  const isDeferred = pendingIds instanceof Set;

  // --- Video player helpers ---
  const stopPlaying = (id) => {
    setPlayTake(p => ({ ...p, [id]: false }));
    setVidHover(h => ({ ...h, [id]: false }));
  };
  const playerInit = (item) => {
    setShowVid(s => ({ ...s, [item.id]: true }));
    setVidHover(h => ({ ...h, [item.id]: true }));
    setPlayTake(p => ({ ...p, [item.id]: true }));
  };
  const handlePlayer = (item) => { if (item.stream_path) playerInit(item); };
  const handlePlay = (id) => {
    if (!vidPlay[id]) setVidPlay(p => ({ ...p, [id]: 'video-play' }));
  };

  // --- Toggle pending selection (deferred mode) ---
  const togglePending = (item) => {
    if (!isDeferred || !onPendingChange) return;
    const next = new Set(pendingIds);
    if (next.has(item.id)) next.delete(item.id);
    else next.add(item.id);
    onPendingChange(next);
  };

  // --- Immediate-commit (legacy / non-deferred mode) ---
  const handleAddRefImmediate = ({ take, file }) => {
    const userRefs = state.generate.userRefs || [];
    if (addUserRef) {
      if (userRefs?.length && userRefs.find(ref => ref.id === take.id)) {
        toast.error("This reference take has already been added.");
        return;
      }
      const type = !state.stories.activeVeneerLUT?.[take.id] && !userRefs.find(ref => ref.type === 'vid')
        ? "vid" : userRefs.length ? "ref" : "first";
      const activeCount = userRefs.filter(ref => ref.active).length;
      const newUserList = [...userRefs, { id: take.id, type, active: totalRefs ? activeCount < totalRefs : true }];
      persistUserState({ patch: { userRefs: newUserList }, dispatch, currentUser, activeStoryRef: state.stories.activeStoryRef });
    }
    dispatch({ type: "UPDATE_TAKE", payload: { ...take, is_ref: true } });
    toast.success("Adding reference...");
    if (callback) callback({ take, file: file || null });
  };

  // --- Upload callback: deferred queues, immediate commits ---
  const handleUpload = ({ take, file }) => {
    if (isDeferred && onUpload) {
      onUpload({ take, file });
    } else {
      handleAddRefImmediate({ take, file });
    }
  };

  // --- Click handler: deferred toggle or immediate commit ---
  const handleItemClick = (item) => {
    if (isDeferred) togglePending(item);
    else handleAddRefImmediate({ take: item });
  };

  // --- Shared item card ---
  const renderItem = (item, key) => {
    const isSelected = isDeferred && pendingIds.has(item.id);
    return (
      <Item
        key={item.id || key}
        sx={{
          cursor: 'pointer',
          position: "relative",
          width: "100%",
          maxWidth: "300px",
          border: isSelected ? "2px solid" : "2px solid transparent",
          borderColor: isSelected ? "primary.main" : "transparent",
          transition: "outline-color 0.15s ease, transform 0.12s ease",
          overflow: "hidden",
          "&:hover .delete": { opacity: 1 },
        }}
        onClick={() => handleItemClick(item)}
      >
        {isSelected && (
          <Box sx={{
            position: "absolute", bottom: 4, right: 4, zIndex: 6,
            color: "primary.main", width: 20, height: 20,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <CheckCircle sx={{ fontSize: 18 }} />
          </Box>
        )
        }
        <Box
          className={`video-wrapper ${vidPlay[item.id] || ''}`}
          onMouseOver={() => handlePlayer(item)}
          onMouseOut={() => setVidHover(h => ({ ...h, [item.id]: false }))}
        >
          <Box component="img" src={item.thumbnail} sx={{ width: "100%", height: "auto", display: "block" }} />
          {showVid[item.id] && (
            <Box className="video">
              <ZebraPlayer
                sx={{ width: "100%", height: "100%" }}
                video_urls={[item.stream_path]}
                controls={false}
                playing={playTake[item.id]}
                trim={[item?.offset_ms]}
                onPlay={() => handlePlay(item.id)}
                onEnded={() => stopPlaying(item.id)}
                loop={vidHover[item.id]}
              />
            </Box>
          )}
          {item.is_ref && (
            <Link className="delete" sx={delStyles} onClick={(e) => { e.stopPropagation(); setConfirmDeleteRef(item.id); }}>
              <Delete sx={{ width: "15px", height: "15px" }} />
            </Link>
          )}
        </Box>
      </Item >
    );
  };

  const gridSx = {
    marginTop: 0, marginLeft: 0, width: "100%",
    maxHeight: maxHeight || "350px", overflow: "auto",
    display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '8px',
  };

  return (
    <>
      <Box key="drop-upload" sx={{ width: "100%", height: state.app.isMobileSize ? "60px" : "120px", marginBottom: "10px" }}>
        <DropUpload
          maxFiles={10}
          type="add"
          mode="reference"
          view="upload"
          text="Upload references — select one or more images / videos"
          label=""
          shotId={shotId}
          callback={handleUpload}
        />
      </Box>

      <TabContext value={tabValue} aria-label="reference tabs">
        <TabList onChange={(_, v) => setTabValue(v)} sx={{ marginBottom: "15px" }}>
          <Tab label="Previous References" value="refs" sx={{ fontSize: '10px', padding: "4px 5px", marginLeft: "0 !important" }} />
          <Tab label="All Takes" value="takes" sx={{ fontSize: '10px', padding: "4px 5px", marginLeft: "0 !important" }} />
        </TabList>

        <TabPanel value="refs" sx={{ padding: 0, width: "100%" }}>
          <Box sx={gridSx}>
            {state.stories.activeRefList?.length === 0 && (
              <Box sx={{ padding: "10px" }}>
                <Typography variant="body2" sx={{ color: "#666", fontStyle: "italic" }}>
                  No active references — choose from All Takes or upload a new reference.
                </Typography>
              </Box>
            )}
            {state.stories.activeRefList?.map((item, key) => renderItem(item, key))}
          </Box>
        </TabPanel>

        <TabPanel value="takes" sx={{ padding: 0, width: "100%" }}>
          <Box sx={gridSx}>
            {state.stories.activeTakeList.map((item, key) => renderItem(item, key))}
          </Box>
        </TabPanel>
      </TabContext>

      <ConfirmDialog
        showDialog={!!confirmDeleteRef}
        onClose={() => setConfirmDeleteRef(null)}
        title="Delete Reference"
        desc="Are you sure you want to delete this reference?"
        doIfConfirmed={() => setConfirmDeleteRef(null)}
      />
    </>
  );
}